/* eslint-disable max-lines-per-function */
import React, { useEffect, useState } from 'react';
import {
  IconButton,
  Tooltip,
  Typography,
  MenuItem,
  Menu,
  InputAdornment,
  OutlinedInput,
} from '@mui/material';
import import_btn from '../../../Assets/images/svg/import_btn.svg';
import sample_file from '../../../Assets/sample_files/reseller_report_sf.csv';
import Modal from '../../../Components/Modal/modal';
import './ResellerImport.scss';
import { getMonths, csvFileToArray} from './utils';
import UseAxios from '../../../Api/http/http';
import { useAuth } from '../../../Contexts/AuthContext';
import { fetchResellerImport } from '../../../Api/request/resellerImport';
import PropTypes from 'prop-types';
import { useSnackbar } from '../../../Components/Snackbar/snackBarProvider';
import { useResellerReport } from '../../../Hooks/useResellerReport';
const Dropdown = ({label, value, updateList, listItem, onChange, error}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuVisible, setMenuVisible] = useState(false);

  const menuItemClick = (itemValue) => {
    setAnchorEl(null);
    setMenuVisible(false);
    onChange(itemValue);
  };

  return (
    <div className="dropdown-item">
      <Typography variant="caption" className="dropdown-heading">
        {label}
      </Typography>
      <OutlinedInput
        onClick={(event) => {
          setAnchorEl(event.currentTarget);
          setMenuVisible(true);
        }}
        className="dropdown-input"
        value={
          typeof value === 'number'
            ? listItem.find((item) => item.number === value)?.name || value
            : value
        }
        sx={{ input: { cursor: 'pointer' } }}
        id="outlined-basic"
        placeholder="Select"
        variant="outlined"
        endAdornment={
          <InputAdornment position="end">
            <i className="pi pi-angle-down"></i>
          </InputAdornment>
        }
      />
      <Menu
        id="basic-menu"
        onClose={() => {
          setAnchorEl(null);
          setMenuVisible(false);
        }}
        className='dropdown-menu'
        anchorEl={anchorEl}
        open={menuVisible}
        disableScrollLock={true}
        sx={{
          '& .MuiPaper-root': {
            width: '260px',
            maxHeight: '250px',
          },
          '& .MuiMenuItem-root': {
            fontSize: '16px',
          },
        }}
      >
        {listItem.map((item) => {
          const itemValue = item.number ? item.number : item;
          return (
            <MenuItem
              key={itemValue}
              value={itemValue}
              onClick={() => {
                menuItemClick(itemValue);
              }}
            >
              {item.name ? item.name : item}
            </MenuItem>
          );
        })}
        {updateList && <MenuItem onClick={updateList}>Load more</MenuItem>}
      </Menu>
      {error && (
        <Typography className="error-message1" color="error">
          Please {label}.
        </Typography>
      )}
    </div>
  );
};

Dropdown.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  listItem: PropTypes.string,
  updateList: PropTypes.func,
  error: PropTypes.bool,
};

const ResellerImport = () => {
  const {setImportError} = useResellerReport();
  const {userProfile} = useAuth();
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');
  const [uploadData, setUploadData] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [yearError, setYearError] = useState(false);
  const [startYear, setStartYear] = useState(new Date().getFullYear());
  const [yearList, setYearList] = useState([]);
  const [monthError, setMonthError] = useState(false);
  const [fileError, setFileError] = useState(false);
  const [fileEmptyError, setFileEmptyError] = useState(false);
  const { snackbarOpen } = useSnackbar();
  const handleClickOpen = () => {
    setOpen(true);
    setYear('');
    setMonth('');
    setStartYear(new Date().getFullYear());
    setYearError(false);
    setMonthError(false);
    setFileError(false);
    setFileEmptyError(false);
    setSelectedFile(null);
  };
  const handleClose = () => {
    setOpen(false);
    setFileError(false);
    setYear('');
    setMonth('');
    setStartYear(new Date().getFullYear());
    setSelectedFile(null);
  };

  const handleMonthChange = (value) => {
    setMonthError(false);
    setMonth(value);
  };

  const handleYearChange = (value) => {
    setYearError(false);
    setYear(value);
  };

  const onImportClick = () => {
    setFileError(false);
    if (year !== '' && month !== '' && selectedFile) {
      if (uploadData.length === 0) {
        setFileEmptyError(true);
        return;
      }
      handleImport();
    } else {
      if (year === '') setYearError(true);
      if (month === '') setMonthError(true);
      if (!selectedFile) setFileError(true);
    }
  };

  useEffect(() => {
    updateYearList();
  }, []);

  const updateYearList = () => {
    const currentYear = new Date().getFullYear() + 3;
    const newStartYear = startYear - 3;
    const list = Array.from(
      { length: currentYear - newStartYear},
      (item, index) => newStartYear + index,
    ).reverse();
    setStartYear(newStartYear);
    setYearList(list);
  };

  const handleImport = async () => {
    try {
      const userInfo = {
        emailId: userProfile.email,
        employeeId: userProfile.employeeId,
        fullName: userProfile.user_name,
        permissions: userProfile.permissions,
      };
      const importData = {
        month: String(month),
        year: String(year),
        uploadData,
      };
      if (uploadData.length === 0) {
        snackbarOpen('Uploaded file is empty. Please select a valid file.', 'error');
        return; // Exit early
      }
      const res = await UseAxios(fetchResellerImport({ userInfo, importData }));
      let data = null;
      if (typeof res.data === 'string'){
        data = JSON.parse(res.data);
      } else {
        data = res.data;
      }
      if (data.success){
        snackbarOpen('File has been successfully imported',
          'success',
        );
        setImportError('');
      } else {
        setImportError(data.message);
      }
      handleClose();
    } catch (error) {
      console.log(error);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFileEmptyError(false); // Clear the empty file error when a new file is selected
      if (file.type !== 'text/csv') {
        setFileError(true); // If file is not CSV, show error
        setSelectedFile(null);
      } else {
        setFileError(false); // Clear file error if file is valid
        setSelectedFile(file);
        const fileReader = new FileReader();
        fileReader.onload = function (fileEvent) {
          const text = fileEvent.target.result;
          if (text) {
            const dataWithIds = csvFileToArray(text).map((item, index) => ({
              ...item,
              id: index + 2,
            }));
            // Check if the file data is empty
            if (dataWithIds.length === 0) {
              setFileEmptyError(true); // Set empty file error
              setUploadData([]); // Clear the uploaded data
            } else {
              setFileEmptyError(false); // Clear empty file error
              setUploadData(dataWithIds); // Set the uploaded data
            }
          }
        };
        fileReader.readAsText(file);
      }
    }
  };

  return (
    <div className={`reseller-report-outer-layer" ${open && 'modal-open'}`}>
      <Tooltip title={'Import'}>
        <IconButton
          id="export-to-excel-btn"
          className="reseller-import-btn"
          onClick={handleClickOpen}
        >
          <img src={import_btn} alt={'Download button'} />
        </IconButton>
      </Tooltip>
      <Modal
        open={open}
        handleClose={handleClose}
        title={
          <div className="modal-header">
            <span>Import</span>
            <a
              href={sample_file}
              download="sample_file.csv"
              className="sample-file-link"
            >
              Sample file
            </a>
          </div>
        }
        dialogBodycontent={
          <>
            <div className="import-instructions">
              <div className="file-upload-container">
                <label htmlFor="file-upload" className="file-upload-button">
                  Choose File
                </label>
                <input
                  id="file-upload"
                  type="file"
                  className="file-input"
                  accept=".csv"
                  onChange={handleFileChange}
                />
                <span className="file-name">
                  {selectedFile ? selectedFile.name : 'No file chosen'}
                </span>
              </div>
              {fileError && (
                <Typography className="error-message1" color="error">
                  Please upload only CSV files.
                </Typography>
              )}
              {fileEmptyError && (
                <Typography className="error-message1" color="error">
    The uploaded file is empty.
                </Typography>
              )}

              <Typography variant="caption" className="file-validation">
                Only files with the following extensions are allowed: csv
              </Typography>
            </div>
            <div className="reseller-import-dropdown-container">
              <Dropdown
                label='Select Year'
                value={year}
                error={yearError}
                onChange={handleYearChange}
                listItem={yearList}
                updateList={updateYearList}
              />
              <Dropdown
                label='Select Month'
                value={month}
                error={monthError}
                onChange={handleMonthChange}
                listItem={getMonths()}
              />
            </div>
          </>
        }
        button1="Cancel"
        button1Action={handleClose}
        button2="Import"
        button2Action={() => {
          // Handle import action here
          onImportClick();
        }}
      />
    </div>
  );
};

export default ResellerImport;
